import { execFileSync, spawn } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import { PassThrough } from 'node:stream'

import * as p from '@clack/prompts'
import type { InitCommandOptions } from '@core-engine/commands/runtime.js'
import { loadConfigFile } from '@core-engine/config/resolver.js'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { getCurrentCliVersion } from '@core-engine/snapshots/store.js'
import { installSetupCancelGuard } from '@core-engine/utils/cancel-guard.js'
import { appendEnvVars, readEnvVar } from '@core-engine/utils/env.js'
import {
  createNextAppCommand,
  detectPackageManager,
  runCommand
} from '@core-engine/utils/package-manager.js'
import { installPromptCheckmarks } from '@core-engine/utils/prompt-theme.js'
import {
  clackLogRows,
  clackPromptRows,
  eraseRowsAbove,
  spinner
} from '@core-engine/utils/spinner.js'
import { detectNextMajorVersion, detectProject } from '@next-adapter/config/detect.js'
import {
  DEFAULT_ADMIN_NAMESPACE,
  deriveMigrationsDir,
  deriveNamespacePaths,
  resolveAdminNamespace,
  validateAdminDashboardPath,
  validateAdminNamespace
} from '@next-adapter/config/namespace.js'
import { getDefaultConfig } from '@next-adapter/config.js'
import {
  openBrowserVercelNeon,
  openBrowserVercelNeonResource,
  promptConnectionString,
  promptServices
} from '@next-adapter/init/prompts/database.js'
import { type PresetPromptResult, promptPresets } from '@next-adapter/init/prompts/presets.js'
import { type ProjectPromptResult, promptProject } from '@next-adapter/init/prompts/project.js'
import {
  addStorageIntegration,
  type DatabaseProvider,
  type DeployProvider,
  promptDeployProvider,
  promptRailwayBucketRegion,
  resolveFlagStorageProvider,
  type StorageProvider,
  validateInitProviderFlags,
  validateResolvedDatabaseProvider
} from '@next-adapter/init/providers.js'
import {
  buildRailwayProviderOverrides,
  runRailwayDeployFlow
} from '@next-adapter/init/railway/deploy.js'
import { ensureRailwayBucket, ensureRailwayPostgres } from '@next-adapter/init/railway/resources.js'
import { createRailwaySession } from '@next-adapter/init/railway/session.js'
import type { RailwayBucketCredentials, RailwaySession } from '@next-adapter/init/railway/types.js'
import { scaffoldApiRoutes } from '@next-adapter/init/scaffolders/api-routes.js'
import { scaffoldAuth } from '@next-adapter/init/scaffolders/auth.js'
import { scaffoldBase } from '@next-adapter/init/scaffolders/base.js'
import { scaffoldBiome } from '@next-adapter/init/scaffolders/biome.js'
import { scaffoldComponents } from '@next-adapter/init/scaffolders/components.js'
import { scaffoldCoreSchemas } from '@next-adapter/init/scaffolders/core-schemas.js'
import { scaffoldDatabase } from '@next-adapter/init/scaffolders/database.js'
import {
  DRIZZLE_KIT_POSTGRES_DRIVER_DEP,
  FRESH_PNPM_ALLOWED_BUILDS,
  getCliDependencySyncPlan,
  getDependencyPlan,
  hasDrizzleKitPostgresDriverDependency,
  installDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import {
  detectDevPort,
  ensureDefaultDevScriptPort
} from '@next-adapter/init/scaffolders/dev-server.js'
import {
  persistBlobReadWriteToken,
  persistDatabaseUrl,
  scaffoldEnv
} from '@next-adapter/init/scaffolders/env.js'
import { scaffoldLayout } from '@next-adapter/init/scaffolders/layout.js'
import { scaffoldNextConfig } from '@next-adapter/init/scaffolders/next-config.js'
import { scaffoldTailwind } from '@next-adapter/init/scaffolders/tailwind.js'
import { scaffoldTsconfig } from '@next-adapter/init/scaffolders/tsconfig.js'
import {
  runVercelBlobFlow,
  runVercelDeployFlow,
  runVercelNeonFlow
} from '@next-adapter/init/vercel/flow.js'
import { readLinkedProjectId } from '@next-adapter/init/vercel/project.js'
import { isEmailProviderIntegration } from '@next-adapter/integration-registry.js'
import {
  type CollectedIntegrationConfig,
  collectIntegrationConfig,
  installIntegrations,
  parseIntegrationList
} from '@next-adapter/integration-runtime.js'
import { installPresets, parsePresetList } from '@next-adapter/preset-runtime.js'
import { runDrizzlePush } from '@next-adapter/utils/drizzle-push.js'
import pc from 'picocolors'
import { buildSeedScript } from './seed.js'

// biome-ignore lint/suspicious/noControlCharactersInRegex: needed to strip ANSI escape sequences from captured command output
const ANSI_ESCAPE_PATTERN = /\u001B\[[0-?]*[ -/]*[@-~]/g
const REMOVABLE_NAMESPACE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export function addVersionToBoxBottomBorder(box: string, version: string): string {
  const lines = box.split('\n')
  const bottomLineIndex = lines.length - 2
  const bottomLine = lines[bottomLineIndex]

  if (!bottomLine) {
    return box
  }

  const leftCornerIndex = bottomLine.indexOf(p.S_CORNER_BOTTOM_LEFT)
  const rightCornerIndex = bottomLine.lastIndexOf(p.S_CORNER_BOTTOM_RIGHT)
  const label = ` v${version} `
  const borderWidth = rightCornerIndex - leftCornerIndex - p.S_CORNER_BOTTOM_LEFT.length

  if (leftCornerIndex === -1 || rightCornerIndex === -1 || borderWidth < label.length) {
    return box
  }

  const leftBorderWidth = Math.floor((borderWidth - label.length) / 2)
  const rightBorderWidth = borderWidth - label.length - leftBorderWidth
  lines[bottomLineIndex] =
    `${bottomLine.slice(0, leftCornerIndex + p.S_CORNER_BOTTOM_LEFT.length)}${p.S_BAR_H.repeat(leftBorderWidth)}${label}${p.S_BAR_H.repeat(rightBorderWidth)}${bottomLine.slice(rightCornerIndex)}`

  return lines.join('\n')
}

function renderInitBanner(): void {
  const output = new PassThrough()
  Object.defineProperty(output, 'columns', { value: process.stdout.columns })

  let box = ''
  output.on('data', (chunk: Buffer) => {
    box += chunk.toString()
  })

  p.box(
    `
▄   ▗ ▗     ▄▖▗     ▗
▙▘█▌▜▘▜▘█▌▛▘▚ ▜▘▀▌▛▘▜▘
▙▘▙▖▐▖▐▖▙▖▌ ▄▌▐▖█▌▌ ▐▖
    `,
    'Setup your Next.js Dashboard',
    {
      rounded: true,
      contentAlign: 'center',
      titleAlign: 'center',
      output
    }
  )

  process.stdout.write(addVersionToBoxBottomBorder(box, getCurrentCliVersion()))
}

function normalizeNamespaceForRemoval(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return
  }

  const segment = value.trim()
  return REMOVABLE_NAMESPACE_PATTERN.test(segment) ? segment : undefined
}

function readNamespaceFromConfigSource(source: string): string | undefined {
  const match = source.match(/\bnamespace\s*:\s*(['"`])([^'"`]+)\1/)
  return normalizeNamespaceForRemoval(match?.[2])
}

function resolveNonInteractiveProject(name: string | undefined): ProjectPromptResult {
  const projectName = name?.trim() || 'my-app'

  if (projectName !== '.' && !/^[a-z0-9_-]+$/i.test(projectName)) {
    throw new Error('Project name can only contain letters, numbers, hyphens, and underscores')
  }

  return { projectName }
}

async function readExistingConfigNamespace(cwd: string): Promise<string | undefined> {
  const configPath = path.resolve(cwd, 'admin.config.ts')
  if (!fs.existsSync(configPath)) {
    return
  }

  const sourceNamespace = readNamespaceFromConfigSource(fs.readFileSync(configPath, 'utf-8'))
  if (sourceNamespace) {
    return sourceNamespace
  }

  try {
    const loadedConfig = await loadConfigFile(configPath)
    return normalizeNamespaceForRemoval(loadedConfig.frameworkConfig?.next?.namespace)
  } catch {
    return
  }
}

export async function resolveForceInitNamespaces(
  cwd: string,
  targetNamespace: string
): Promise<string[]> {
  const namespaces = new Set<string>([validateAdminNamespace(targetNamespace)])
  const hasConfig = fs.existsSync(path.resolve(cwd, 'admin.config.ts'))
  const existingNamespace = await readExistingConfigNamespace(cwd)

  if (existingNamespace) {
    namespaces.add(existingNamespace)
  } else if (hasConfig) {
    namespaces.add(DEFAULT_ADMIN_NAMESPACE)
  }

  return [...namespaces]
}

export function removeExistingAdminPaths(
  cwd: string,
  namespaces: string | Iterable<string>
): number {
  const segments = Array.from(
    new Set(
      (typeof namespaces === 'string' ? [namespaces] : [...namespaces]).flatMap((namespace) => {
        const segment = normalizeNamespaceForRemoval(namespace)
        return segment ? [segment] : []
      })
    )
  )
  const nukeDirs = [
    ...segments.flatMap((segment) => [segment, `app/(${segment})`, `src/app/(${segment})`]),
    '.betterstart'
  ]
  const nukeFiles = ['admin.config.ts', 'ADMIN.md', 'drizzle.config.ts']
  let removed = 0

  for (const dir of nukeDirs) {
    const fullPath = path.resolve(cwd, dir)
    if (fs.existsSync(fullPath)) {
      fs.rmSync(fullPath, { recursive: true, force: true })
      removed++
    }
  }

  for (const file of nukeFiles) {
    const fullPath = path.resolve(cwd, file)
    if (fs.existsSync(fullPath)) {
      fs.unlinkSync(fullPath)
      removed++
    }
  }

  return removed
}

export async function runInitCommand(
  name: string | undefined,
  options: InitCommandOptions
): Promise<void> {
  // Completed prompt steps and successful spinner stops render with a green
  // check instead of clack's hardcoded diamond for the whole init run.
  installPromptCheckmarks()

  // Ctrl-C anywhere during setup prints "Setup cancelled." and exits cleanly;
  // disposed before the managed dev server takes over signal forwarding.
  const disposeCancelGuard = installSetupCancelGuard()

  renderInitBanner()

  let selectedPresets: ReturnType<typeof parsePresetList>
  let selectedIntegrations: ReturnType<typeof parseIntegrationList>
  let flagStorageProvider: StorageProvider | undefined
  try {
    validateInitProviderFlags(options)
    selectedPresets = options.presets ? parsePresetList(options.presets) : []
    selectedIntegrations = options.integrations ? parseIntegrationList(options.integrations) : []
    flagStorageProvider = resolveFlagStorageProvider(options.storageProvider, selectedIntegrations)
    if (flagStorageProvider) {
      selectedIntegrations = addStorageIntegration(selectedIntegrations, flagStorageProvider)
    }
  } catch (error) {
    disposeCancelGuard()
    p.log.error(error instanceof Error ? error.message : String(error))
    process.exit(1)
  }

  let cwd = process.cwd()
  // Name used for the Vercel project when the Neon provisioning flow runs.
  // Set in both the existing-project and fresh-project branches below.
  let projectName = path.basename(cwd)
  let forceMode = Boolean(options.force)
  let namespace = DEFAULT_ADMIN_NAMESPACE
  if (options.namespace) {
    try {
      namespace = validateAdminNamespace(options.namespace)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
      process.exit(1)
    }
  }

  if (!options.yes && !options.namespace) {
    const defaultDashboardPath = resolveAdminNamespace(DEFAULT_ADMIN_NAMESPACE).routePath
    const namespaceInput = await p.text({
      message: 'Enter the dashboard path',
      placeholder: `eg. ${defaultDashboardPath}`,
      defaultValue: defaultDashboardPath,
      validate(value) {
        try {
          validateAdminDashboardPath(value ?? defaultDashboardPath)
          return
        } catch (error) {
          return error instanceof Error ? error.message : String(error)
        }
      }
    })
    if (p.isCancel(namespaceInput)) {
      p.cancel('Setup cancelled.')
      process.exit(0)
    }
    namespace = validateAdminDashboardPath(namespaceInput)
  }

  let project = detectProject(cwd, namespace)
  let pm = detectPackageManager(cwd)
  let isFreshProject = false

  // Determine srcDir
  let srcDir: boolean
  if (project.isExisting) {
    srcDir = project.hasSrcDir

    if (!project.hasTypeScript) {
      p.log.error('TypeScript is required. Please add a tsconfig.json first.')
      process.exit(1)
    }

    if (forceMode) {
      const nuked = removeExistingAdminPaths(cwd, await resolveForceInitNamespaces(cwd, namespace))
      if (nuked > 0) {
        p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
      }
      // Re-detect project after wipe
      project = detectProject(cwd, namespace)
    } else if (project.conflicts.length > 0) {
      const conflictLines = project.conflicts.map((c) => `${pc.yellow('▲')} ${c}`)
      conflictLines.push(
        '',
        pc.dim(`Use ${pc.bold('--force')} to remove existing admin files before scaffolding.`)
      )
      p.note(conflictLines.join('\n'), pc.yellow('Conflicts'))
      if (!options.yes) {
        const proceed = await p.confirm({
          message: [
            `Continue with ${pc.bold(pc.cyan('--force'))}?`,
            `${pc.cyan('│')}  ${pc.dim('This will force overwrite the existing admin code.')}`,
            pc.cyan('│')
          ].join('\n'),
          initialValue: true
        })
        if (p.isCancel(proceed) || !proceed) {
          p.cancel('Setup cancelled.')
          process.exit(0)
        }

        forceMode = true
        const nuked = removeExistingAdminPaths(
          cwd,
          await resolveForceInitNamespaces(cwd, namespace)
        )
        if (nuked > 0) {
          p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
        }
        project = detectProject(cwd, namespace)
      }
    }
  } else {
    let projectPrompt: ProjectPromptResult
    try {
      projectPrompt = options.yes ? resolveNonInteractiveProject(name) : await promptProject(name)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
      process.exit(1)
    }
    srcDir = false

    // Ask for preferred package manager
    if (!options.yes) {
      const pmChoice = await p.select({
        message: 'Which package manager do you want to use?',
        options: [
          { value: 'pnpm' as const, label: 'pnpm', hint: 'recommended' },
          { value: 'npm' as const, label: 'npm' },
          { value: 'bun' as const, label: 'bun' }
        ]
      })
      if (p.isCancel(pmChoice)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }
      pm = pmChoice
    }

    // Bootstrap the exact create-next-app package through npm without installing yet.
    // BetterStart installs once after the generated package.json has a supported pnpm pin.
    const displayName =
      projectPrompt.projectName === '.' ? path.basename(cwd) : projectPrompt.projectName
    projectName = displayName

    const { bin, prefix } = createNextAppCommand()
    const cnaArgs = [
      ...prefix,
      projectPrompt.projectName,
      '--yes',
      '--typescript',
      '--tailwind',
      '--app',
      '--turbopack',
      '--biome',
      '--react-compiler',
      `--use-${pm}`,
      '--skip-install'
    ]
    if (srcDir) cnaArgs.push('--src-dir')
    else cnaArgs.push('--no-src-dir')

    const createNextAppSpinner = spinner()
    createNextAppSpinner.start(`Creating a Next.js app (latest)`)

    const createNextAppResult = await runQuietCommand(bin, cnaArgs, {
      cwd,
      timeoutMs: 120_000
    })

    if (!createNextAppResult.success) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      if (createNextAppResult.output.trim().length > 0) {
        process.stderr.write(`${createNextAppResult.output.trimEnd()}\n`)
      }
      p.log.error(createNextAppResult.error)
      p.log.info(
        `You can create the project manually:\n  ${pc.cyan(`npx create-next-app@latest ${projectPrompt.projectName} --typescript --tailwind --app`)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      process.exit(1)
    }

    // Move into the new project directory
    cwd = path.resolve(cwd, projectPrompt.projectName)

    // Verify the project was actually created
    const hasPackageJson = fs.existsSync(path.join(cwd, 'package.json'))
    const hasNextConfig = ['next.config.ts', 'next.config.js', 'next.config.mjs'].some((f) =>
      fs.existsSync(path.join(cwd, f))
    )

    if (!hasPackageJson || !hasNextConfig) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      p.log.error(
        'create-next-app completed but the project was not created. This can happen with nested npx calls.'
      )
      const manualCmd = `npx create-next-app@latest ${projectPrompt.projectName} --typescript --tailwind --app`
      p.log.info(
        `Create the project manually:\n  ${pc.cyan(manualCmd)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      process.exit(1)
    }

    createNextAppSpinner.clear()
    project = detectProject(cwd, namespace)
    isFreshProject = true
  }

  // Database, storage, and deployment are independent provider decisions. A
  // Railway session is created lazily and shared by every selected Railway
  // resource so auth and project selection only happen once.
  let databaseUrl: string | undefined
  let dismissVercelSignedInNote: (() => void) | undefined
  let railwaySessionPromise: Promise<RailwaySession> | undefined
  let railwayDatabaseServiceName: string | undefined
  let railwayBucketResourceName: string | undefined
  let railwayBucketUrlStyle: string | undefined
  const existingDbUrl = readExistingDbUrl(cwd)
  const getRailwaySession = (): Promise<RailwaySession> => {
    railwaySessionPromise ??= createRailwaySession({
      cwd,
      projectName,
      interactive: !options.yes,
      workspace: options.railwayWorkspace,
      project: options.railwayProject,
      environment: options.railwayEnvironment,
      env: process.env
    }).catch((error) => {
      railwaySessionPromise = undefined
      throw error
    })
    return railwaySessionPromise
  }

  let promptedManualUrl: string | undefined
  let databaseProvider: DatabaseProvider
  if (options.databaseUrl) {
    databaseProvider = 'manual'
  } else if (options.databaseProvider) {
    databaseProvider = options.databaseProvider
  } else if (existingDbUrl || options.yes) {
    databaseProvider = 'manual'
  } else {
    const servicesResult = await promptServices()
    databaseProvider = servicesResult.provider
    promptedManualUrl = servicesResult.url
  }

  try {
    validateResolvedDatabaseProvider(databaseProvider, options)
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    process.exit(1)
  }

  if (databaseProvider === 'manual') {
    const candidate = options.databaseUrl ?? existingDbUrl ?? promptedManualUrl
    if (candidate && !isValidDbUrl(candidate)) {
      p.log.error(
        `Invalid database URL. Must start with ${pc.cyan('postgres://')} or ${pc.cyan('postgresql://')}`
      )
      process.exit(1)
    }
    if (candidate) {
      databaseUrl = candidate
      if (existingDbUrl === candidate && !options.databaseUrl) {
        p.log.info(
          `Using the existing DATABASE_URL from .env.local ${pc.dim(`(${maskDbUrl(candidate)})`)}`
        )
      }
    } else if (!options.yes) {
      databaseUrl = await promptConnectionString()
    }
  } else if (databaseProvider === 'vercel') {
    const flow = await runVercelNeonFlow({
      cwd,
      projectName,
      interactive: !options.yes,
      plan: options.databasePlan,
      env: process.env
    })
    if (flow.ok && flow.databaseUrl) {
      databaseUrl = flow.databaseUrl
      persistDatabaseUrl(cwd, databaseUrl)
      dismissVercelSignedInNote = flow.dismissSignedInNote
    } else if (options.yes) {
      p.log.error(
        flow.ok
          ? 'Created a Neon database, but DATABASE_URL could not be retrieved from Vercel.'
          : 'Vercel database provisioning did not complete.'
      )
      process.exit(1)
    } else if (flow.ok) {
      openBrowserVercelNeonResource(flow.dashboardUrl ?? flow.resourceUrl)
      databaseUrl = await promptConnectionString()
      persistDatabaseUrl(cwd, databaseUrl)
    } else {
      p.log.info('Falling back to a manual database connection string.')
      openBrowserVercelNeon()
      databaseUrl = await promptConnectionString()
    }
  } else {
    p.log.warn(
      'Railway Postgres is container-backed. Plan and test your own backups, upgrades, and high-availability strategy before production use.'
    )
    try {
      const session = await getRailwaySession()
      const postgres = await ensureRailwayPostgres(session, cwd)
      databaseUrl = postgres.databaseUrl
      railwayDatabaseServiceName = postgres.service.name
      persistDatabaseUrl(cwd, databaseUrl)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (options.yes) {
        p.log.error(`Railway database provisioning failed: ${message}`)
        process.exit(1)
      }
      p.log.warn(`Railway database provisioning failed: ${message}`)
      p.log.info('Falling back to a manual database connection string.')
      databaseUrl = await promptConnectionString()
    }
  }

  // Integration secrets are collected inline by promptPresets, right after each
  // provider is selected, so input happens up front — before scaffolding — and
  // installIntegrations never stops to prompt mid-scaffold. Provisioned Vercel
  // Blob tokens are persisted immediately; other collected values are written
  // after scaffoldEnv below.
  let collectedIntegrationConfig: CollectedIntegrationConfig = {
    sections: [],
    overwriteKeys: new Set<string>()
  }
  let presetSelection: Omit<PresetPromptResult, 'integrationConfig'>

  const mergeCollectedIntegrationConfig = (next: CollectedIntegrationConfig): void => {
    collectedIntegrationConfig.sections.push(...next.sections)
    for (const key of next.overwriteKeys) {
      collectedIntegrationConfig.overwriteKeys.add(key)
    }
  }

  const provisionRailwayBucket = async (): Promise<{
    ok: boolean
    config?: CollectedIntegrationConfig
  }> => {
    try {
      const region =
        options.railwayBucketRegion ?? (options.yes ? 'iad' : await promptRailwayBucketRegion())
      const session = await getRailwaySession()
      const bucket = await ensureRailwayBucket(session, cwd, {
        name: 'media',
        region
      })
      const config = railwayBucketIntegrationConfig(bucket.credentials)
      appendEnvVars(cwd, config.sections, config.overwriteKeys)
      railwayBucketResourceName = bucket.resource.name
      railwayBucketUrlStyle = bucket.credentials.urlStyle
      return { ok: true, config }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      p.log.warn(`Railway bucket provisioning failed: ${message}`)
      return { ok: false }
    }
  }

  if (selectedPresets.length > 0 || selectedIntegrations.length > 0 || flagStorageProvider) {
    const storage = flagStorageProvider ?? 'local'
    presetSelection = {
      presets: selectedPresets,
      integrations: selectedIntegrations,
      storage
    }

    if (storage === 'r2') {
      const missingR2Keys = R2_ENV_KEYS.filter((key) => !readEnvVar(cwd, key)?.trim())
      if (options.yes && missingR2Keys.length > 0) {
        p.log.error(
          `Cloudflare R2 is missing required environment variables: ${missingR2Keys.join(', ')}.`
        )
        process.exit(1)
      }
      mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['r2']))
    } else if (storage === 'railway-bucket') {
      if (hasRailwayBucketConfig(cwd)) {
        mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['railway-bucket']))
      } else {
        const flow = await provisionRailwayBucket()
        if (flow.ok && flow.config) {
          mergeCollectedIntegrationConfig(flow.config)
        } else if (options.yes) {
          p.log.error('Railway bucket provisioning did not complete.')
          process.exit(1)
        }
      }
    } else if (storage === 'vercel-blob' && !readEnvVar(cwd, 'BLOB_READ_WRITE_TOKEN')?.trim()) {
      const flow = await runVercelBlobFlow({
        cwd,
        projectName,
        interactive: !options.yes,
        env: process.env
      })
      if (flow.ok && flow.token) {
        // Persisted silently — like DATABASE_URL, no "Saved …" notice.
        persistBlobReadWriteToken(cwd, flow.token)
        collectedIntegrationConfig.sections.push({
          header: 'Storage (Vercel Blob)',
          vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: flow.token }]
        })
        collectedIntegrationConfig.overwriteKeys.add('BLOB_READ_WRITE_TOKEN')
      } else if (options.yes) {
        p.log.error('Vercel Blob provisioning did not complete.')
        process.exit(1)
      } else {
        p.log.warn(
          'Continuing without a Blob token — add BLOB_READ_WRITE_TOKEN to .env.local from the store on vercel.com/dashboard/stores.'
        )
        collectedIntegrationConfig.sections.push({
          header: 'Storage (Vercel Blob)',
          vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: '' }]
        })
      }
    } else if (storage === 'vercel-blob') {
      mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['vercel-blob']))
    }
  } else if (options.yes) {
    presetSelection = { presets: [], integrations: [], storage: 'local' }
  } else {
    dismissVercelSignedInNote?.()
    const promptResult = await promptPresets(cwd, {
      provisionVercelBlob: () =>
        runVercelBlobFlow({ cwd, projectName, interactive: true, env: process.env }),
      provisionRailwayBucket
    })
    collectedIntegrationConfig = promptResult.integrationConfig
    presetSelection = {
      presets: promptResult.presets,
      integrations: promptResult.integrations,
      storage: promptResult.storage
    }
  }

  // Build config
  const nextMajorVersion = detectNextMajorVersion(cwd)
  const config: ResolvedBetterstartConfig = {
    ...getDefaultConfig(srcDir, nextMajorVersion)
  }
  config.frameworkConfig.next.namespace = namespace
  config.frameworkConfig.next.paths = deriveNamespacePaths(namespace, srcDir)
  config.paths = config.frameworkConfig.next.paths
  config.database.migrationsDir = deriveMigrationsDir(namespace)

  // Run scaffolders — grouped under one spinner whose message names the
  // current step; no summary card is printed afterwards.
  const s = spinner()

  s.start('Directory structure')
  scaffoldBase({
    cwd,
    config,
    includeEmailTemplatesDir: presetSelection.integrations.some(isEmailProviderIntegration)
  })

  s.message('TypeScript aliases')
  scaffoldTsconfig(cwd, config)

  s.message('Tailwind CSS')
  scaffoldTailwind(cwd, srcDir, namespace)

  s.message('Dev server')
  ensureDefaultDevScriptPort(cwd)

  s.message('Environment variables')
  scaffoldEnv(cwd, { databaseUrl, namespace })
  if (collectedIntegrationConfig.sections.length > 0) {
    appendEnvVars(
      cwd,
      collectedIntegrationConfig.sections,
      collectedIntegrationConfig.overwriteKeys
    )
  }

  s.message('Database')
  const dbFiles = scaffoldDatabase({ cwd, config })

  s.message('Authentication')
  scaffoldAuth({ cwd, config })

  s.message('Components')
  scaffoldComponents({ cwd, config })

  s.message('Pages & layouts')
  scaffoldLayout({ cwd, config })

  s.message('API routes')
  scaffoldApiRoutes({ cwd, config })

  s.message('Linter')
  if (project.linter.type === 'none') {
    scaffoldBiome(cwd, project.linter)
  }

  s.message('Next.js config')
  const nextConfigResult = scaffoldNextConfig({ cwd, nextMajorVersion })

  s.clear()
  if (nextConfigResult.status === 'unsupported') {
    p.log.warn('The Next.js config could not be updated automatically — review it manually.')
  }

  // If drizzle.config.ts already existed (wasn't created), offer to update it
  const drizzleConfigPath = path.join(cwd, 'drizzle.config.ts')
  if (!dbFiles.includes('drizzle.config.ts') && fs.existsSync(drizzleConfigPath)) {
    if (forceMode) {
      const { readNamespacedTemplate } = await import('@next-adapter/template-reader.js')
      fs.writeFileSync(
        drizzleConfigPath,
        readNamespacedTemplate('drizzle.config.ts', namespace),
        'utf-8'
      )
      p.log.success('Updated drizzle.config.ts')
    } else if (!options.yes) {
      const overwrite = await p.confirm({
        message: 'drizzle.config.ts already exists. Overwrite with latest version?',
        initialValue: true
      })
      if (!p.isCancel(overwrite) && overwrite) {
        const { readNamespacedTemplate } = await import('@next-adapter/template-reader.js')
        fs.writeFileSync(
          drizzleConfigPath,
          readNamespacedTemplate('drizzle.config.ts', namespace),
          'utf-8'
        )
        p.log.success('Updated drizzle.config.ts')
      }
    }
  }

  // Install dependencies (async so spinner can animate)
  const coreDependencyPlan = getDependencyPlan([], [], project.linter.type === 'none')
  const cliDependencyPlan = getCliDependencySyncPlan(cwd)
  s.start('Installing dependencies, This may take a moment')
  const depsResult = await installDependenciesAsync({
    cwd,
    pm,
    ...(isFreshProject && pm === 'pnpm'
      ? { pnpmAllowedBuilds: [...FRESH_PNPM_ALLOWED_BUILDS] }
      : {}),
    dependencies: Array.from(
      new Set([...coreDependencyPlan.dependencies, ...(cliDependencyPlan?.dependencies ?? [])])
    ),
    devDependencies: Array.from(
      new Set([
        ...coreDependencyPlan.devDependencies,
        ...(cliDependencyPlan?.devDependencies ?? [])
      ])
    )
  })
  if (depsResult.success) {
    s.stop('')
  } else {
    s.stop('Failed to install dependencies')
    p.log.warning(depsResult.error ?? 'Unknown error')
    p.log.info(
      `You can install them manually:\n  ${pc.cyan(`${pm} add ${depsResult.dependencies.join(' ')}`)}\n  ${pc.cyan(`${pm} add -D ${depsResult.devDeps.join(' ')}`)}`
    )
    disposeCancelGuard()
    process.exit(1)
  }

  // Generate core-owned schemas
  process.stdout.write('\x1B[2A\x1B[J')
  s.start('Generating core schemas')
  const coreSchemasResult = scaffoldCoreSchemas({ cwd, config })
  s.stop('')
  process.stdout.write('\x1B[2A\x1B[J')

  // Install selected presets through the shared preset pipeline
  const presetInstallResult =
    presetSelection.presets.length > 0
      ? (() => {
          s.start(`Installing presets: ${presetSelection.presets.join(', ')}`)
          return installPresets({
            cwd,
            config,
            pm,
            presetIds: presetSelection.presets,
            interactive: !options.yes,
            includeBiome: project.linter.type === 'none'
          })
        })()
      : Promise.resolve({ installed: [], skipped: [], warnings: [], config })
  const resolvedPresetInstallResult = await presetInstallResult
  if (presetSelection.presets.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  const integrationInstallResult =
    presetSelection.integrations.length > 0
      ? (() => {
          s.start(`Installing integrations: ${presetSelection.integrations.join(', ')}`)
          return installIntegrations({
            cwd,
            config: resolvedPresetInstallResult.config,
            pm,
            integrationIds: presetSelection.integrations,
            // Secrets were already collected up front and persisted/written to
            // .env.local, so the install runs without prompting (rule: no
            // mid-scaffold input).
            interactive: false,
            includeBiome: project.linter.type === 'none'
          })
        })()
      : Promise.resolve({
          installed: [],
          skipped: [],
          warnings: [],
          config: resolvedPresetInstallResult.config
        })
  const resolvedIntegrationInstallResult = await integrationInstallResult
  if (presetSelection.integrations.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  writeConfigFile(cwd, resolvedIntegrationInstallResult.config)
  const usesLocalStorage = resolvedIntegrationInstallResult.config.storage.provider === 'local'

  // No install summary card — only warnings still surface.
  for (const err of coreSchemasResult.errors) {
    p.log.warn(`Core schemas: ${err}`)
  }
  for (const warning of resolvedPresetInstallResult.warnings) {
    p.log.warn(warning)
  }
  for (const warning of resolvedIntegrationInstallResult.warnings) {
    p.log.warn(warning)
  }
  if (usesLocalStorage) {
    p.log.warn(
      'Local filesystem storage is only for development or self-hosted persistent-disk deployments. Use Vercel Blob, Railway Bucket, or Cloudflare R2 for hosted production.'
    )
  }

  // Push database schema after core/preset generation so entity tables are included
  let dbPushed = false
  if (depsResult.success && options.skipMigration && hasDbUrl(cwd)) {
    p.log.info(`Skipping database schema push ${pc.dim('(--skip-migration)')}`)
  } else if (depsResult.success && hasDbUrl(cwd)) {
    let driverReady = hasDrizzleKitPostgresDriverDependency(cwd)

    if (!driverReady) {
      s.start(
        `Installing Drizzle Kit Postgres driver dependency (${DRIZZLE_KIT_POSTGRES_DRIVER_DEP})`
      )
      const driverResult = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: [],
        devDependencies: [DRIZZLE_KIT_POSTGRES_DRIVER_DEP]
      })

      if (!driverResult.success) {
        s.stop('Database push failed')
        p.log.warning(driverResult.error ?? `Failed to install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP}`)
        p.log.info(
          `Install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP} manually, then run: ${pc.cyan(drizzlePushCommand(pm))}`
        )
      } else {
        driverReady = hasDrizzleKitPostgresDriverDependency(cwd)
        s.stop(`Drizzle Kit Postgres driver dependency installed`)
      }
    }

    if (driverReady) {
      // A successful push prints nothing of its own — no "Running …" banner
      // and no "Database schema pushed" line; failures still report below.
      // Terminal-attached pushes are silent while drizzle-kit connects, so a
      // spinner covers that window; it is cleared the moment drizzle produces
      // visible output so its rename prompts render on a clean line.
      const useTerminalForDbPush = shouldUseTerminalForDbPush(options)
      let dbSpinnerVisible = true
      const clearDbSpinner = () => {
        if (!dbSpinnerVisible) return
        dbSpinnerVisible = false
        s.clear()
      }
      s.start(
        useTerminalForDbPush ? 'Setting up database' : 'Pushing database schema (drizzle-kit push)'
      )

      const pushResult = await runDrizzlePush(cwd, {
        interactive: useTerminalForDbPush,
        onOutput: clearDbSpinner
      })
      if (pushResult.success) {
        if (useTerminalForDbPush) {
          // A prompt-free push leaves the spinner up through the
          // reachability probe, which is just as silent.
          const verification = await verifyDatabaseReachable(cwd)
          clearDbSpinner()
          if (!verification.success) {
            p.log.warning(verification.error)
            p.log.error('Database was not reachable. Aborting setup.')
            process.exit(1)
          }
        } else {
          clearDbSpinner()
        }
        dbPushed = true
      } else {
        if (dbSpinnerVisible) {
          dbSpinnerVisible = false
          s.stop('Database push failed')
        }
        const pushError = pushResult.error ?? 'Unknown error'
        p.log.warning(pushError)
        if (isDatabaseReachabilityError(pushError)) {
          p.log.error('Database was not reachable. Aborting setup.')
          process.exit(1)
        }
        p.log.info(`You can run it manually: ${pc.cyan(drizzlePushCommand(pm))}`)
      }
    }
  }

  // Auto-seed admin user (only when db push succeeded and interactive mode)
  let seedEmail: string | undefined
  let seedPassword: string | undefined
  let seedSuccess = false
  let adminAccountReady = false

  if (dbPushed && options.skipAdminCreation) {
    p.log.info(
      `Skipping admin user creation ${pc.dim(`(use ${betterstartExecCommand(pm, 'seed')} later)`)}`
    )
  }

  if (dbPushed && !options.yes && !options.skipAdminCreation) {
    const adminDir = config.paths?.admin ?? './admin'
    const authBasePath = `${resolveAdminNamespace(namespace).apiPath}/auth`
    let replaceExistingAdmin = false
    // The admin check is another silent database round-trip; keep the
    // "Setting up database" cover until its result is known, clearing before
    // any prompt or warning below renders.
    s.start('Setting up database')
    const adminCheck = await checkExistingAdmin(cwd, adminDir, authBasePath)
    s.clear()

    if (adminCheck.error) {
      p.log.warning(`Could not verify existing admin account ${pc.dim(`(${adminCheck.error})`)}`)
      if (isDatabaseReachabilityError(adminCheck.error)) {
        p.log.error('Database was not reachable. Aborting setup.')
        process.exit(1)
      }
    } else if (adminCheck.existingAdmin) {
      const existingAdminLabel = formatAdminIdentity(adminCheck.existingAdmin)
      const adminAction = await p.select({
        message: 'Found an already existing admin account. Do you want to replace it or skip?',
        options: [
          {
            value: 'replace' as const,
            label: 'Replace',
            hint: existingAdminLabel
          },
          {
            value: 'skip' as const,
            label: 'Skip'
          }
        ]
      })

      if (p.isCancel(adminAction)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }

      if (adminAction === 'skip') {
        adminAccountReady = true
        p.log.info(`Keeping existing admin account ${pc.dim(`(${existingAdminLabel})`)}`)
      } else {
        replaceExistingAdmin = true
      }
    }

    if (!adminAccountReady) {
      const credentials = await p.group(
        {
          email: () =>
            p.text({
              message: 'Admin email',
              placeholder: 'admin@example.com',
              validate: (v) => {
                if (!v || !v.includes('@')) return 'Please enter a valid email'
              }
            }),
          password: () =>
            p.password({
              message: 'Admin password',
              validate: (v) => {
                if (!v || v.length < 8) return 'Password must be at least 8 characters'
              }
            })
        },
        {
          onCancel: () => {
            p.cancel('Setup cancelled.')
            process.exit(0)
          }
        }
      )

      seedEmail = credentials.email
      seedPassword = credentials.password
      const credentialPromptRows =
        clackPromptRows('Admin email', credentials.email) +
        clackPromptRows('Admin password', p.S_PASSWORD_MASK.repeat(credentials.password.length))
      let rowsBelowCredentialPrompts = 0

      let seedOverwriteMode: SeedOverwriteMode | undefined = replaceExistingAdmin
        ? 'admin'
        : undefined
      s.start(replaceExistingAdmin ? 'Replacing admin user' : 'Creating admin user')
      let seedResult = await runSeed(
        cwd,
        adminDir,
        authBasePath,
        credentials.email,
        credentials.password,
        seedOverwriteMode
      )

      if (seedResult.existingUser) {
        const existingAccountMessage = `${pc.yellow('▲')} An account already exists for ${credentials.email}`
        s.stop(existingAccountMessage)
        rowsBelowCredentialPrompts += clackLogRows(existingAccountMessage)
        const replaceAccountMessage = 'Replace the existing account with this email?'
        const replace = await p.confirm({
          message: replaceAccountMessage,
          initialValue: false
        })
        rowsBelowCredentialPrompts += clackPromptRows(
          replaceAccountMessage,
          replace === true ? 'Yes' : 'No'
        )
        if (!p.isCancel(replace) && replace) {
          seedOverwriteMode = 'email'
          s.start('Replacing admin user')
          seedResult = await runSeed(
            cwd,
            adminDir,
            authBasePath,
            credentials.email,
            credentials.password,
            seedOverwriteMode
          )
        }
      }

      if (seedResult.success) {
        const seedSuccessMessage =
          seedOverwriteMode === 'admin'
            ? `Admin user replaced`
            : `Admin user ${pc.green(seedEmail)} successfully created`
        s.stop(seedSuccessMessage)
        rowsBelowCredentialPrompts += clackLogRows(seedSuccessMessage)
        eraseRowsAbove(credentialPromptRows, rowsBelowCredentialPrompts)
        seedSuccess = true
        adminAccountReady = true
      } else if (seedResult.error) {
        s.stop(`Failed to create admin user`)
        p.note(
          `${pc.red(seedResult.error)}\n\nRun manually: ${pc.cyan(betterstartExecCommand(pm, 'seed'))}`,
          pc.red('Seed failed')
        )
        if (isDatabaseReachabilityError(seedResult.error)) {
          p.log.error('Database was not reachable. Aborting setup.')
          process.exit(1)
        }
      }
    }
  }

  // Git init + commit for fresh projects
  if (isFreshProject) {
    s.start('Creating initial git commit')
    try {
      execFileSync('git', ['init'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['add', '.'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['commit', '-m', 'Initial commit from BetterStart'], {
        cwd,
        stdio: 'pipe'
      })
      s.clear()
    } catch {
      s.stop('Git commit skipped')
    }
  }

  const adminNamespace = resolveAdminNamespace(namespace)
  const adminLoginUrl = `http://localhost:${detectDevPort(cwd)}${adminNamespace.routePath}/login`

  let deployProvider: DeployProvider
  if (options.deployProvider) {
    deployProvider = options.deployProvider
  } else if (options.yes) {
    deployProvider = 'none'
  } else {
    const initialDeployProvider: DeployProvider = railwaySessionPromise
      ? 'railway'
      : readLinkedProjectId(cwd)
        ? 'vercel'
        : 'none'
    deployProvider = await promptDeployProvider(initialDeployProvider)
  }

  if (deployProvider === 'vercel') {
    const deployFlow = await runVercelDeployFlow({
      cwd,
      projectName,
      interactive: !options.yes,
      env: process.env
    })
    if (!deployFlow.ok) {
      if (options.yes) {
        p.log.error('Vercel deploy did not complete.')
        process.exit(1)
      }
      p.log.warn('Vercel deploy did not complete; continuing.')
    }
  } else if (deployProvider === 'railway') {
    try {
      const session = await getRailwaySession()
      const deployFlow = await runRailwayDeployFlow({
        session,
        cwd,
        service: options.railwayService,
        providerOverrides: buildRailwayProviderOverrides({
          databaseServiceName: railwayDatabaseServiceName,
          bucketResourceName: railwayBucketResourceName,
          bucketUrlStyle: railwayBucketUrlStyle
        })
      })
      if (!deployFlow.ok) {
        if (deployFlow.detail) p.log.message(pc.dim(deployFlow.detail))
        if (options.yes) {
          p.log.error('Railway deploy did not complete.')
          process.exit(1)
        }
        p.log.warn('Railway deploy did not complete; continuing.')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (options.yes) {
        p.log.error(`Railway deploy failed: ${message}`)
        process.exit(1)
      }
      p.log.warn(`Railway deploy failed: ${message}`)
    }
  }

  // Offer to start the dev server (interactive mode only)
  if (!options.yes && !options.skipDevServerStart) {
    const devCmd = runCommand(pm, 'dev')
    const startDev = await p.confirm({
      message: 'Start the development server?',
      initialValue: true
    })

    if (!p.isCancel(startDev) && startDev) {
      // The dev server owns signals from here: Ctrl-C must forward to the
      // child and shut it down gracefully, not cancel an already-done setup.
      disposeCancelGuard()
      await startManagedDevServer(cwd, devCmd, adminLoginUrl, {
        email: seedSuccess && seedEmail ? seedEmail : undefined,
        password: seedSuccess && seedPassword ? seedPassword : undefined
      })
      return
    }
  }

  disposeCancelGuard()
  p.outro('Done!')
}

// ============================================================================
// Helpers
// ============================================================================

/** Validate a database connection URL */
function isValidDbUrl(url: string): boolean {
  return url.startsWith('postgres://') || url.startsWith('postgresql://')
}

/** Read DATABASE_URL from .env.local, returning the value if it's a real URL. */
function readExistingDbUrl(cwd: string): string | undefined {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) return undefined
  const content = fs.readFileSync(envPath, 'utf-8')
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const [key, ...rest] = trimmed.split('=')
    if (key?.trim() === 'DATABASE_URL') {
      const val = rest
        .join('=')
        .replace(/^['"]|['"]$/g, '')
        .trim()
      if (
        val.length > 0 &&
        !val.startsWith('your_') &&
        val !== 'postgresql://...' &&
        isValidDbUrl(val)
      ) {
        return val
      }
    }
  }
  return undefined
}

/** Mask a database URL for display: show host only. */
function maskDbUrl(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.protocol}//${parsed.host}/***`
  } catch {
    return 'postgres://***'
  }
}

/** Check if DATABASE_URL is set in .env.local */
function hasDbUrl(cwd: string): boolean {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) return false
  const content = fs.readFileSync(envPath, 'utf-8')
  // Check the var exists and has a non-placeholder value
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const [key, ...rest] = trimmed.split('=')
    if (key?.trim() === 'DATABASE_URL') {
      const val = rest.join('=').trim()
      const unquoted = val.replace(/^['"]|['"]$/g, '')
      return unquoted.length > 0 && !unquoted.startsWith('your_') && unquoted !== 'postgresql://...'
    }
  }
  return false
}

const RAILWAY_BUCKET_ENV_KEYS = [
  'BETTERSTART_RAILWAY_BUCKET_ENDPOINT',
  'BETTERSTART_RAILWAY_BUCKET_ACCESS_KEY_ID',
  'BETTERSTART_RAILWAY_BUCKET_SECRET_ACCESS_KEY',
  'BETTERSTART_RAILWAY_BUCKET_NAME',
  'BETTERSTART_RAILWAY_BUCKET_REGION',
  'BETTERSTART_RAILWAY_BUCKET_URL_STYLE'
] as const

const R2_ENV_KEYS = [
  'BETTERSTART_R2_ACCOUNT_ID',
  'BETTERSTART_R2_ACCESS_KEY_ID',
  'BETTERSTART_R2_SECRET_ACCESS_KEY',
  'BETTERSTART_R2_BUCKET_NAME',
  'BETTERSTART_R2_PUBLIC_URL'
] as const

function hasRailwayBucketConfig(cwd: string): boolean {
  return RAILWAY_BUCKET_ENV_KEYS.every((key) => Boolean(readEnvVar(cwd, key)?.trim()))
}

function railwayBucketIntegrationConfig(
  credentials: RailwayBucketCredentials
): CollectedIntegrationConfig {
  return {
    sections: [
      {
        header: 'Storage (Railway Bucket)',
        vars: [
          { key: 'BETTERSTART_RAILWAY_BUCKET_ENDPOINT', value: credentials.endpoint },
          {
            key: 'BETTERSTART_RAILWAY_BUCKET_ACCESS_KEY_ID',
            value: credentials.accessKeyId
          },
          {
            key: 'BETTERSTART_RAILWAY_BUCKET_SECRET_ACCESS_KEY',
            value: credentials.secretAccessKey
          },
          { key: 'BETTERSTART_RAILWAY_BUCKET_NAME', value: credentials.bucketName },
          { key: 'BETTERSTART_RAILWAY_BUCKET_REGION', value: credentials.region },
          { key: 'BETTERSTART_RAILWAY_BUCKET_URL_STYLE', value: credentials.urlStyle }
        ]
      }
    ],
    overwriteKeys: new Set(RAILWAY_BUCKET_ENV_KEYS)
  }
}

interface SeedResult {
  success: boolean
  error: string | null
  existingUser?: string
}

interface ExistingAdmin {
  email: string
  name?: string
}

interface ExistingAdminCheckResult {
  existingAdmin?: ExistingAdmin
  error: string | null
}

type SeedOverwriteMode = 'admin' | 'email'

interface QuietCommandResult {
  success: boolean
  error: string
  output: string
}

function shouldUseTerminalForDbPush(options: InitCommandOptions): boolean {
  return (
    !options.yes &&
    process.env.CI !== 'true' &&
    Boolean(process.stdin.isTTY) &&
    Boolean(process.stdout.isTTY)
  )
}

function betterstartExecCommand(
  pm: ReturnType<typeof detectPackageManager>,
  subcommand: string
): string {
  switch (pm) {
    case 'pnpm':
      return `pnpm betterstart ${subcommand}`.trim()
    case 'yarn':
      return `yarn betterstart ${subcommand}`.trim()
    case 'bun':
      return `bunx betterstart ${subcommand}`.trim()
    default:
      return `npx betterstart ${subcommand}`.trim()
  }
}

function drizzlePushCommand(pm: ReturnType<typeof detectPackageManager>): string {
  switch (pm) {
    case 'pnpm':
      return 'pnpm drizzle-kit push'
    case 'yarn':
      return 'yarn drizzle-kit push'
    case 'bun':
      return 'bunx drizzle-kit push'
    default:
      return 'npx drizzle-kit push'
  }
}

function formatAdminIdentity(admin: ExistingAdmin): string {
  return admin.name?.trim() ? `${admin.name} (${admin.email})` : admin.email
}

function isDatabaseReachabilityError(error: string): boolean {
  const normalized = error.toLowerCase()
  return [
    'admin account check timed out',
    'connect_timeout',
    'connection refused',
    'could not connect to database',
    'database connection failed',
    'database was not reachable',
    'econnrefused',
    'econnreset',
    'enotfound',
    'etimedout',
    'no route to host',
    'timeout',
    'timed out'
  ].some((pattern) => normalized.includes(pattern))
}

async function verifyDatabaseReachable(cwd: string): Promise<QuietCommandResult> {
  const databaseUrl = readExistingDbUrl(cwd)
  if (!databaseUrl) {
    return {
      success: false,
      error: 'Database URL is missing or invalid',
      output: ''
    }
  }

  const result = await runQuietCommand(
    process.execPath,
    [
      '-e',
      `
const { createRequire } = require('node:module')
const requireFromProject = createRequire(process.cwd() + '/package.json')
const { Client } = requireFromProject('pg')
const client = new Client({
  connectionString: process.env.DATABASE_URL,
  connectionTimeoutMillis: 10000
})

async function main() {
  try {
    await client.connect()
    await client.query('select 1')
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.error('Database connection failed: ' + message)
    process.exitCode = 1
  } finally {
    await client.end().catch(() => {})
  }
}

main().catch((error) => {
  const message = error instanceof Error ? error.message : String(error)
  console.error('Database connection failed: ' + message)
  process.exitCode = 1
})
`
    ],
    {
      cwd,
      timeoutMs: 15_000,
      env: {
        DATABASE_URL: databaseUrl
      }
    }
  )

  if (result.success) {
    return result
  }

  const error = parseSeedError(result.output, result.error)
  return {
    ...result,
    error
  }
}

function runSeedScript(
  cwd: string,
  adminDir: string,
  authBasePath: string,
  envOverrides: NodeJS.ProcessEnv
): Promise<{
  code: number | null
  stdout: string
  stderr: string
  timeout: boolean
  spawnError?: string
}> {
  const scriptsDir = path.join(cwd, adminDir, 'scripts')
  const seedPath = path.join(scriptsDir, 'seed.ts')

  if (!fs.existsSync(scriptsDir)) {
    fs.mkdirSync(scriptsDir, { recursive: true })
  }
  fs.writeFileSync(seedPath, buildSeedScript(authBasePath), 'utf-8')

  const cleanup = () => {
    try {
      fs.unlinkSync(seedPath)
      if (fs.existsSync(scriptsDir) && fs.readdirSync(scriptsDir).length === 0) {
        fs.rmdirSync(scriptsDir)
      }
    } catch {
      // Not critical
    }
  }

  return new Promise((resolve) => {
    const tsxBin = path.join(cwd, 'node_modules', '.bin', 'tsx')
    const child = spawn(tsxBin, [seedPath], {
      cwd,
      stdio: 'pipe',
      env: {
        ...process.env,
        ...envOverrides
      }
    })
    let stdout = ''
    let stderr = ''
    child.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString()
    })
    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })
    const timeout = setTimeout(() => {
      child.kill()
      cleanup()
      resolve({ code: null, stdout, stderr, timeout: true })
    }, 30_000)
    child.on('close', (code) => {
      clearTimeout(timeout)
      cleanup()
      resolve({ code, stdout, stderr, timeout: false })
    })
    child.on('error', (err) => {
      clearTimeout(timeout)
      cleanup()
      resolve({ code: null, stdout, stderr, timeout: false, spawnError: err.message })
    })
  })
}

/** Check whether an admin account already exists before prompting for credentials. */
async function checkExistingAdmin(
  cwd: string,
  adminDir: string,
  authBasePath: string
): Promise<ExistingAdminCheckResult> {
  const result = await runSeedScript(cwd, adminDir, authBasePath, {
    SEED_CHECK_ADMIN: 'true'
  })

  if (result.timeout) {
    return { error: 'Admin account check timed out after 30 seconds' }
  }

  if (result.spawnError) {
    return { error: parseSeedError(result.stdout, result.spawnError) }
  }

  if (result.code === 0) {
    return { error: null }
  }

  if (result.code === 3) {
    const rawAdmin = result.stdout.match(/EXISTING_ADMIN:(.+)/)?.[1]?.trim()
    if (!rawAdmin) {
      return { error: 'Admin account check returned an unreadable response' }
    }

    try {
      const existingAdmin = JSON.parse(rawAdmin) as ExistingAdmin
      return { existingAdmin, error: null }
    } catch {
      return { error: 'Admin account check returned an unreadable response' }
    }
  }

  return { error: parseSeedError(result.stdout, result.stderr) }
}

/** Write seed script, run it with tsx, clean up. Returns success/error. */
async function runSeed(
  cwd: string,
  adminDir: string,
  authBasePath: string,
  email: string,
  password: string,
  overwriteMode?: SeedOverwriteMode
): Promise<SeedResult> {
  const result = await runSeedScript(cwd, adminDir, authBasePath, {
    SEED_EMAIL: email,
    SEED_PASSWORD: password,
    SEED_NAME: 'Admin',
    ...(overwriteMode ? { SEED_OVERWRITE_MODE: overwriteMode } : {})
  })

  if (result.timeout) {
    return { success: false, error: 'Seed timed out after 30 seconds' }
  }

  if (result.spawnError) {
    return { success: false, error: parseSeedError(result.stdout, result.spawnError) }
  }

  if (result.code === 0) {
    return { success: true, error: null }
  }

  if (result.code === 2) {
    // Exit code 2 = user already exists
    const name = result.stdout.match(/EXISTING_USER:(.+)/)?.[1]?.trim()
    return { success: false, error: null, existingUser: name ?? email }
  }

  return { success: false, error: parseSeedError(result.stdout, result.stderr) }
}

/** Extract a human-readable error from seed script output */
function parseSeedError(stdout: string, stderr: string): string {
  // Check for common patterns in stderr/stdout
  const combined = `${stdout}\n${stderr}`

  // "Seed failed: <message>" from the catch handler
  const seedFailed = combined.match(/Seed failed:\s*(.+)/)?.[1]?.trim()
  if (seedFailed) return seedFailed

  // "Failed to create user" from auth API failure
  if (combined.includes('Failed to create user')) return 'Auth API failed to create user'

  // Connection errors
  if (isDatabaseReachabilityError(combined)) {
    return 'Could not connect to database'
  }
  if (combined.includes('DATABASE_URL')) return 'Database URL is missing or invalid'
  if (combined.includes('password authentication failed'))
    return 'Database authentication failed — check your connection string'
  if (combined.includes('does not exist') && combined.includes('relation'))
    return 'Database tables not found — run drizzle-kit push first'
  if (combined.includes('MODULE_NOT_FOUND') || combined.includes('Cannot find module'))
    return 'Missing dependencies — run your package manager install first'

  // Fall back to first meaningful line of stderr
  const firstLine = stderr
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith('at ') && !l.startsWith('node:'))
  if (firstLine) return firstLine

  return 'Unknown error — run betterstart seed for details'
}

function runQuietCommand(
  bin: string,
  args: string[],
  options: {
    cwd: string
    timeoutMs: number
    env?: NodeJS.ProcessEnv
  }
): Promise<QuietCommandResult> {
  return new Promise((resolve) => {
    const child = spawn(bin, args, {
      cwd: options.cwd,
      stdio: 'pipe',
      env: {
        ...process.env,
        ...options.env
      }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false

    child.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString()
    })

    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
    }, options.timeoutMs)

    child.on('close', (code, signal) => {
      clearTimeout(timeout)

      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')

      if (timedOut) {
        resolve({
          success: false,
          error: `Command timed out after ${Math.floor(options.timeoutMs / 1000)} seconds`,
          output
        })
        return
      }

      if (signal) {
        resolve({
          success: false,
          error: `Command was terminated by ${signal}`,
          output
        })
        return
      }

      if (code === 0) {
        resolve({
          success: true,
          error: '',
          output
        })
        return
      }

      resolve({
        success: false,
        error: `Command exited with code ${code ?? 'unknown'}`,
        output
      })
    })

    child.on('error', (err) => {
      clearTimeout(timeout)
      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')
      resolve({
        success: false,
        error: err.message,
        output
      })
    })
  })
}

function stripAnsi(value: string): string {
  return value.replace(ANSI_ESCAPE_PATTERN, '')
}

function printAdminReadyNote(state: {
  adminLoginUrl: string
  adminEmail?: string
  adminPassword?: string
}): void {
  const lines = [`Admin: ${pc.cyan(state.adminLoginUrl)}`]

  if (state.adminEmail && state.adminPassword) {
    lines.unshift(`Password: ${pc.cyan(state.adminPassword)}`)
    lines.unshift(`Admin user: ${pc.cyan(state.adminEmail)}`)
  }

  p.note(lines.join('\n'), 'Admin ready')
}

function shouldSuppressDevServerStartupLine(line: string): {
  suppress: boolean
  isReadyLine: boolean
} {
  const plain = stripAnsi(line).trim()

  if (plain.length === 0) {
    return { suppress: true, isReadyLine: false }
  }

  if (plain.includes('Ready in ')) {
    return { suppress: true, isReadyLine: true }
  }

  if (
    plain.startsWith('> ') ||
    plain.startsWith('$ ') ||
    plain.startsWith('▲ Next.js ') ||
    plain.startsWith('- ') ||
    plain.startsWith('· ') ||
    plain.startsWith('⨯ ')
  ) {
    return { suppress: true, isReadyLine: false }
  }

  if (plain.startsWith('✓ ') && !plain.includes('Ready in ')) {
    return { suppress: true, isReadyLine: false }
  }

  return { suppress: false, isReadyLine: false }
}

function pipeManagedDevServerStream(
  stream: NodeJS.ReadableStream | null | undefined,
  target: NodeJS.WriteStream,
  state: {
    suppressStartup: boolean
    readyLogged: boolean
    adminLoginUrl: string
    adminEmail?: string
    adminPassword?: string
  }
): void {
  if (!stream) return

  let buffer = ''

  const flushBuffer = (flushRemainder = false) => {
    let newlineIndex = buffer.indexOf('\n')

    while (newlineIndex !== -1) {
      const line = buffer.slice(0, newlineIndex + 1)
      buffer = buffer.slice(newlineIndex + 1)
      writeLine(line)
      newlineIndex = buffer.indexOf('\n')
    }

    if (flushRemainder && buffer.length > 0) {
      writeLine(buffer)
      buffer = ''
    }
  }

  const writeLine = (line: string) => {
    if (!state.suppressStartup) {
      target.write(line)
      return
    }

    const verdict = shouldSuppressDevServerStartupLine(line)

    if (verdict.isReadyLine) {
      if (!state.readyLogged) {
        printAdminReadyNote(state)
        state.readyLogged = true
      }
      return
    }

    if (verdict.suppress) {
      return
    }

    if (state.readyLogged) {
      state.suppressStartup = false
    }

    target.write(line)
  }

  stream.on('data', (chunk: Buffer | string) => {
    buffer += chunk.toString()
    flushBuffer()
  })

  stream.on('end', () => {
    flushBuffer(true)
  })
}

function startManagedDevServer(
  cwd: string,
  devCmd: string,
  adminLoginUrl: string,
  adminCredentials?: {
    email?: string
    password?: string
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    const [bin, ...args] = devCmd.split(' ')
    const child = spawn(bin, args, {
      cwd,
      stdio: ['inherit', 'pipe', 'pipe']
    })

    const state = {
      suppressStartup: true,
      readyLogged: false,
      adminLoginUrl,
      adminEmail: adminCredentials?.email,
      adminPassword: adminCredentials?.password
    }

    const forwardSignal = (signal: NodeJS.Signals) => {
      if (!child.killed) {
        child.kill(signal)
      }
    }

    const cleanup = () => {
      process.off('SIGINT', forwardSignal)
      process.off('SIGTERM', forwardSignal)
      process.off('SIGHUP', forwardSignal)
    }

    process.on('SIGINT', forwardSignal)
    process.on('SIGTERM', forwardSignal)
    process.on('SIGHUP', forwardSignal)

    pipeManagedDevServerStream(child.stdout, process.stdout, state)
    pipeManagedDevServerStream(child.stderr, process.stderr, state)

    child.on('close', (code, signal) => {
      cleanup()

      if (signal) {
        process.kill(process.pid, signal)
        return
      }

      if (typeof code === 'number' && code !== 0) {
        process.exitCode = code
      }

      resolve()
    })

    child.on('error', (err) => {
      cleanup()
      reject(err)
    })
  })
}
